import { Injectable } from '@angular/core';
import { IRecipientEntity } from '../../model/api/recipients/recipient.entity.interface';
import { APP_CONFIG } from '../../environment/index';
import { RecipientMapper } from './recipient.mapper';
import { IRecipient } from '../../model/models/recipient';
import { UpdateRecipientResponse } from '../../model/api/recipients/update.recipient.response.interface';
import { StorageProvider } from '../storage/storage';
import { IRecipientBankSearchRequest } from '../../model/models/recipient/recipient-bank-search-request.interface';
import { IRecipientBank, RecipientBankDetails, RecipientBankLocation } from '../../model/models/recipient/recipient-bank.interface';
import { IRecipientBankSearchResponseEntity } from '../../model/api/recipients/recipient-bank-search-response.entity.interface';
import { HttpProvider } from '../http/http';
import { RouteProvider } from '../route/route';
import { DeliveryMethodType } from '../../model/types/delivery-method-type';
import { OrderProvider } from '../../providers/order/order';
import { TranslateService } from '@ngx-translate/core';
import { Transaction } from 'model/myria/app/transaction';
import { LoggerProvider, LoggerEvent } from '../logger/logger';
import { RmtSnackbarComponent } from '../../components/rmt-snackbar/rmt-snackbar';
import { FeatureFlagTypes, UtilsProvider } from '../myria/utils-provider';
import { BankDepositSmartRoutingProvider } from '../bank-deposit-smart-routing/bank-deposit-smart-routing';
import { IBankDepositIsSmartRoutingEnabledRequestEntity } from '../../model/api/bank-deposit';
import { ErrorCodeType, ErrorKeyType } from '../../model/types/error-code-type';
import { PixIdType } from '../../model/types/vpa-type';
import { AccountFormatPatterns } from '../../model/types/account-format-patterns';
import { DELIVERY_METHOD } from '../../providers/myria/app-variables';
import { IVop, IRegisteredName, VopOutcome, VopConsent, getVopOutcome } from '../../model/types/vop-type';
import { SegmentProvider } from '../analytics/segment';
import { AnalyticsEventType, AccountValidationType, AccountValidationDecision } from '../../model/types/analytics.const';
/**
 * @description Recipient Service Provider
 */
@Injectable()
export class RecipientProvider {
  initialized: boolean = false;
  initPromise: Promise<any>;
  initResolve;
  newRecipient;
  justCreatedRecipient;
  deletedRecipientNumber: string;

  recipients: IRecipient[];
  lastRecipients: IRecipient[];

  lastRecipientsListDate = null;

  constructor(private httpProvider: HttpProvider,
              private translate: TranslateService,
              private routeProvider: RouteProvider,
              private utilsProvider: UtilsProvider,
              private analytics: SegmentProvider,
              private bankDepositSmartRoutingProvider: BankDepositSmartRoutingProvider,
              private storageProvider?: StorageProvider) {
  }

  getLastRecipients() {
    return this.lastRecipients;
  }

  setNewRecipient(recipient) {
    this.lastRecipients = this.recipients;
    this.newRecipient = recipient;
  }

  getNewRecipient() {
    return this.newRecipient;
  }

  setJustCreatedRecipient(recipient) {
    this.justCreatedRecipient = recipient
  }

  getJustCreatedRecipient() {
    return this.justCreatedRecipient
  }

  setDeletedRecipientNumber(number) {
    this.lastRecipients = this.recipients;
    this.deletedRecipientNumber = number;
  }

  getDeletedRecipientNumber() {
    return this.deletedRecipientNumber;
  }

  initializeRecipients(forceReload: boolean = false, initResolve = null) {
    if (this.initialized && !forceReload)
      return;
    else if (this.initPromise && !initResolve)
      return;

    const calls = [];
    calls.push(this.httpProvider.get(`${APP_CONFIG.remittanceApiBaseUrl}recipients`));
    
    if (!this.initPromise)
      this.initPromise = new Promise((resolve) => {
        if (!initResolve)
          initResolve = resolve;
      });    
    this.httpProvider.multipleCalls(calls, ([recipients]: [IRecipientEntity[]]) => {
      this.initPromise = null;
      this.initialized = true;
      this.recipients = RecipientMapper.fromRecipientsEntity(recipients, this.storageProvider);
      this.lastRecipientsListDate = new Date().getTime();
      initResolve();
      return true;
    }, (err) => {
      if (err.status === 401 || err.status === 403) // these are already handled and shouldn't cause a reload
        this.initPromise = null;
      else
        window.setTimeout(() => {
          this.initializeRecipients(true, initResolve);
        }, 10000);
    });
  }

  logout() {
    this.initialized = false;
    this.initPromise = null;
    this.newRecipient = null;
    this.deletedRecipientNumber = null;
    this.recipients = null;
    this.lastRecipientsListDate = null;
    this.vopEditedRecipients.clear();
  }

  ensureInitializedRecipients() {
    this.initializeRecipients();
    if (this.initPromise)
      return this.initPromise;
    return null;
  }  

  async getRecipients(forceReload: boolean = false): Promise<IRecipient[]> {
    if (forceReload && this.lastRecipientsListDate) {
      const diff = (new Date().getTime() - this.lastRecipientsListDate) / 1000;
      if (diff < 120) {
        const skip = await this.utilsProvider.hasFeatureFlag(FeatureFlagTypes.SkipRecipientListOverride);
        if (skip)
          LoggerProvider.info(this, `Skipped override recipients list force reload although threshold is too low: ${diff} seconds`);
        else {
          LoggerProvider.info(this, `Override recipients list force reload since threshold is too low: ${diff} seconds`);
          forceReload = false;
        }
      }
    }
    if (forceReload) {
      this.initializeRecipients(true);
    }
    await this.ensureInitializedRecipients();
    if (forceReload) {
      if (this.routeProvider.getCurrentTabName() === this.routeProvider.pages.RecipientListPage.page) {
        const page = this.routeProvider.getCurrentTabPage();
        if (page)
          page.setRecipients(this.recipients);
      }
    }
    return this.recipients;
  }  

  async getBasicRecipientByIdFromList(id){
    const recipients = await this.getRecipients()
    if(recipients)
      return recipients.find(recipient=> recipient.id === id);
    return null;
  }

  addRecipient(recipient: any): Promise<IRecipient> {
    return this.httpProvider.post(`${APP_CONFIG.remittanceApiBaseUrl}recipient`, recipient, (res) => {
      return RecipientMapper.fromRecipientEntity(res.recipientResponse);
    });
  }

  getRecipientInitials(recipient: IRecipient) {
    let initial = '';
    if (recipient.firstName) {
      initial += recipient.firstName.substr(0,1);
    }
    if (recipient.lastName1) {
      initial += recipient.lastName1.substr(0,1);
    }
    if (recipient.lastName2) {
      initial += recipient.lastName2.substr(0,1);
    }
    return initial.substr(0,2).toUpperCase();
  }

  /** Sources the bank list from CST banksearch while the corridor gate is on, mapped to the same shape either way. The CDM list always stays legacy. */
  async searchRecipientBanks(searchCriteria: IRecipientBankSearchRequest, selectedBank?: IRecipientBank, isCDMBankList: boolean = false): Promise<any> {
    const corridor: IBankDepositIsSmartRoutingEnabledRequestEntity = {
      countryTo: searchCriteria.countryTo,
      currencyFrom: searchCriteria.currencyFrom,
      currencyTo: searchCriteria.currencyTo,
      amountFrom: searchCriteria.amountFrom,
      amountTo: searchCriteria.amountTo,
      stateFrom: searchCriteria.stateFrom,
      stateTo: searchCriteria.stateTo
    };
    if (!isCDMBankList && this.bankDepositSmartRoutingProvider.isEnabledForCorridor(corridor)) {
      const smartRoutingBanks = await this.searchSmartRoutingBanks(searchCriteria, selectedBank);
      if (smartRoutingBanks)
        return smartRoutingBanks;
    }
    return this.httpProvider.put(`${APP_CONFIG.remittanceApiBaseUrl}bank/search/v2`, RecipientMapper.toRecipientBankSearchRequestEntity(searchCriteria, isCDMBankList), 
      (banksResponse: IRecipientBankSearchResponseEntity) => {
        return {
          banks: RecipientMapper.fromRecipientBankEntities(banksResponse, selectedBank),
          hasMoreData: banksResponse.hasMoreData,
          defaultData: banksResponse.defaultData,
          skippedData: banksResponse.skippedData
        };
      });
  }

  /** Null when banksearch could not answer, so the caller falls back to the legacy list. */
  private async searchSmartRoutingBanks(searchCriteria: IRecipientBankSearchRequest, selectedBank?: IRecipientBank): Promise<any> {
    try {
      const res = await this.bankDepositSmartRoutingProvider.searchBanks(
        RecipientMapper.toBankDepositSearchRequestEntity(searchCriteria, this.bankDepositSmartRoutingProvider.searchCorridor));
      if (!res || !res.IsSuccess || !res.EntityList) {
        LoggerProvider.error(this, LoggerEvent.ProviderException, 'Bank deposit smart routing bank search returned no list, falling back to the legacy bank search', res);
        return null;
      }
      return {
        banks: RecipientMapper.fromBankDepositSearchEntities(res, selectedBank),
        hasMoreData: res.HasMoreData,
        defaultData: res.DefaultData,
        skippedData: res.SkippedData
      };
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Bank deposit smart routing bank search failed, falling back to the legacy bank search');
      return null;
    }
  }

  async searchBankCities(recipientCountry: string, bankId: number, recipientCity:string = ""): Promise<RecipientBankLocation[]> {
    const result = await this.httpProvider.get(`${APP_CONFIG.remittanceApiBaseUrl}bank/${bankId}/cities`, {
      countryTo: recipientCountry,
      city: recipientCity,
    })
    if (result)
      return result;
    throw new Error('Invalid Results')
  }

  async searchBankBranchLocationID(recipientCountry: string, recipientCity, recipientState: string, bankId: number): Promise<RecipientBankDetails[]> {
    const result = await this.httpProvider.get(`${APP_CONFIG.remittanceApiBaseUrl}bank/${bankId}/locations`, {
      countryTo: recipientCountry,
      city: recipientCity,
      state: recipientState,
    })
    if (result)
      return result;
    throw new Error('Invalid Results')
  }

  saveRecipientBankAccount(recipient: IRecipient, currencyTo: string, amountFrom: number, deliveryMethod = null): Promise<any> {
    return this.httpProvider.put(`${APP_CONFIG.remittanceApiBaseUrl}recipient/bank-deposit`, RecipientMapper.toSaveRecipientBankDepositRequestEntity(recipient, currencyTo, amountFrom, deliveryMethod));
  }

  async validateBankDeposit(order: any): Promise<IVop | null> {
    const recipient = order.recipient || {};
    const request = {
      recipientId: recipient.id,
      reSendOrderId: order.orderId,
      modifyingOrderNumber: order.modifyingOrderNumber,
      deliveryMethod: order.deliveryMethodInternal,
      paymentMethod: order.paymentMethod,
      bankAccountFormatId: recipient.bankAccountFormatId,
      amountFrom: order.amountFrom,
      currencyTo: order.currencyTo,
      hasRecipientDebitCard: !!order.recipientDebitCard,
    };
    return this.httpProvider.post(`${APP_CONFIG.remittanceApiBaseUrl}recipient/bank-deposit/validate`, request, (res: any) => (res && res.vop) ? res.vop : null);
  }

  updateRecipient(recipient: IRecipient): Promise<IRecipient> {
    return this.httpProvider.put(`${APP_CONFIG.remittanceApiBaseUrl}recipient/${recipient.id}`, recipient, ({ recipientResponse }: UpdateRecipientResponse) => {
      const recipient = RecipientMapper.fromRecipientEntity(recipientResponse);
      recipient.loaded = true;
      return recipient;
    });
  }

  modifyRecipientName(recipientData): Promise<any> {
    return this.httpProvider.put(`${APP_CONFIG.API_URL}api/Beneficiary`, recipientData);
  }

  deleteRecipient(id: number): Promise<any> {
    return this.httpProvider.delete(`${APP_CONFIG.remittanceApiBaseUrl}recipient/${id}`);
  }

  getRecipient(id: number): Promise<IRecipient> {
    return this.httpProvider.get(`${APP_CONFIG.remittanceApiBaseUrl}recipient/${id}`, null, (res: IRecipientEntity) => {      
      const recipient = RecipientMapper.fromRecipientEntity(res, this.storageProvider);
      recipient.loaded = true;
      return recipient;
    });
  }

  getNameEditEligibility(recipientId: number, orderNumber: string): Promise <any> {
    return this.httpProvider.get(APP_CONFIG.remittanceApiBaseUrl + 'recipient/nameediteligibility', {RecipientId: recipientId, OrderNumber: orderNumber});
  }

  formatAccountNumber(recipient: any, bankAccountNumber: string, orderProvider: OrderProvider, isBank: boolean = true): string {
    let formattedAccountNumber = '';
    const maxTotalLength = 22;
    const ellipsis = '...';
    const formattedBase = orderProvider.getMaskAccountNumber(bankAccountNumber)

    // Calculate the allowed length for bankName
    const allowedBankNameLength = maxTotalLength - formattedBase.length - ellipsis.length;
  
    let bankName = isBank ? recipient.bankName : '';
    
    // Truncate bankName if it exceeds the allowed length
    if (bankName.length > allowedBankNameLength) {
      bankName = bankName.slice(0, allowedBankNameLength) + ellipsis;
    }
    
    if (bankName) {
      formattedAccountNumber = `${bankName} ${formattedBase}`;;
    } else {
      formattedAccountNumber = `${formattedBase}`;
    }
    return formattedAccountNumber;
  }

  getRecipientText(paramObj: any, orderProvider: OrderProvider, transaction: Transaction, fromActivity: boolean = false) {
    let {recipient, deliveryMethod, deliveryMethodLabel, countries} = paramObj;
    let recipientDeliveryMethod = recipient.lastDeliveryMethod;

    if (deliveryMethod && deliveryMethod !== recipient.lastDeliveryMethod) {
      recipientDeliveryMethod = deliveryMethod;
    }

    if (recipientDeliveryMethod) {
      deliveryMethodLabel = this.getDeliveryMethodLabel(recipientDeliveryMethod);
      const bankAccountNumber = recipient.bankAccountNumberUnitary ? recipient.bankAccountNumberUnitary : recipient.bankAccountNumber; 

      if (recipientDeliveryMethod === DeliveryMethodType.BankDeposit && fromActivity) {
        
        var formattedAccountNumber = '';
        if (recipient.bankName) {
          formattedAccountNumber = this.formatAccountNumber(recipient, bankAccountNumber, orderProvider);
        }
        
        deliveryMethodLabel = formattedAccountNumber;
        return deliveryMethodLabel;
      }

      if (recipientDeliveryMethod === DeliveryMethodType.BankDeposit && bankAccountNumber && !fromActivity && !RecipientMapper.isDirectDebitPayoutAgent(recipient)) {
        return deliveryMethodLabel.replace('{{bankAccount}}', bankAccountNumber.replace(/\*/g, ''));
      } else if (recipientDeliveryMethod === DeliveryMethodType.DebitCard && bankAccountNumber && !fromActivity && RecipientMapper.isDirectDebitPayoutAgent(recipient)) {
        return deliveryMethodLabel.replace('{{debitCard}}', bankAccountNumber.replace(/\*/g, ''));
      }

      if (recipientDeliveryMethod === DeliveryMethodType.MobilePayment) {
        let mobileWalletNo = recipient.mobileWalletAccountNo;
        if (!mobileWalletNo)
          mobileWalletNo = '';
        mobileWalletNo = mobileWalletNo.replace(/\*/g, '');
        if (!fromActivity)
          return deliveryMethodLabel.replace('{{walletAccountId}}', mobileWalletNo);
        else          
          return (transaction ? transaction.PayAgentName : '') + ' ' + this.formatAccountNumber(recipient, mobileWalletNo, orderProvider, false);
      }

      if (recipientDeliveryMethod === DeliveryMethodType.PagoMovil && recipient.virtualPaymentAccountNo) {
        return `${DELIVERY_METHOD.PAGO_MOVIL.Text} ${orderProvider.getPagoMovilAccountNumber(recipient)}`;
      }

      if (orderProvider.isVpaDeliveryMethod(recipientDeliveryMethod) && (recipient.virtualPaymentAccountNo || fromActivity)) {
        let vpa = recipient.virtualPaymentAccountNo;
        if (!vpa)
          vpa = '';
        if (recipient.virtualPaymentType === "PIX" && recipient.pixIdType === PixIdType.CPFNumber && vpa.length === 11) 
          return deliveryMethodLabel.replace('{{vpaAccountId}}', this.utilsProvider.formatValueWithPattern(vpa, AccountFormatPatterns.CPFNumber));
        return deliveryMethodLabel.replace('{{vpaAccountId}}', vpa.replace(/\*/g, ''));
      }
      
      if (recipientDeliveryMethod === DeliveryMethodType.OfficePickup || recipientDeliveryMethod === DeliveryMethodType.HomeDelivery || recipientDeliveryMethod === DeliveryMethodType.CashPickup) {
        let location = recipient.city;
        if (!location)
          location = recipient.state;
        if (!location)
          location = recipient.country;
        if (!location)
          location = '';
        return deliveryMethodLabel.replace('{{city}}', location);
      }


      if (countries && countries[recipient.country]) {
        return countries[recipient.country];
      }

      return deliveryMethodLabel;
    }

    if (countries && countries[recipient.country]) {
      return countries[recipient.country];
    }

    return '&nbsp;';
  }

  getDeliveryMethodLabel(deliveryMethod): string {
    let label = '';


    switch (deliveryMethod) {
      case DeliveryMethodType.BankDeposit:
        label = this.translate.instant('SendMoneyPaymentPage.BankAccountInfo');
        break;
      case DeliveryMethodType.MobilePayment:
        label = this.translate.instant('SendMoneyPaymentPage.WalletAccountInfo');
        break;
      case DeliveryMethodType.OfficePickup: 
      case DeliveryMethodType.CashPickup:
        label = this.translate.instant('SendMoneyPaymentPage.CashPickupInfo');
        break;
      case DeliveryMethodType.HomeDelivery:
        label = this.translate.instant('SendMoneyPaymentPage.HomeDeliveryInfo');
        break;
      case DeliveryMethodType.DebitCard:
        label = this.translate.instant('SendMoneyPaymentPage.DebitCardInfo');
        break;
      case DeliveryMethodType.PIX:
      case DeliveryMethodType.Alipay:
      case DeliveryMethodType.UPI:
        label = this.translate.instant('SendMoneyPaymentPage.VPAPaymentInfo').replace('{{vpaName}}', deliveryMethod);
        break;
      case DeliveryMethodType.BreB:
        label = this.translate.instant('SendMoneyPaymentPage.VPAPaymentInfo').replace('{{vpaName}}', DELIVERY_METHOD.BREB.Text);
        break;
    }

    return label;
  }

  notifyPossibleChanges() {
    this.lastRecipientsListDate = null;
  }

  async fetchRecipients() {
    this.httpProvider.get(`${APP_CONFIG.remittanceApiBaseUrl}recipients`);
  }

  checkBankRoutingFormatForIndia(ex, bank, recipient): boolean {
    return ex.errorCode && ex.errorCode === ErrorCodeType.ModelValidation
      && ex.errorKey && ex.errorKey === ErrorKeyType.BankRoutingCode
      && recipient
      && recipient.country === 'IN'
      && bank.form
      && bank.form.form
      && bank.form.form.controls
      && bank.form.form.controls.bankRoutingCode;
  }

  isInvalidBankAccountError(ex): boolean {
    if (!ex || !ex.errorCode)
      return false;
    return ex.errorCode === ErrorCodeType.InvalidUnitaryAccountNumber
      || ex.errorCode === ErrorCodeType.RecipientBankDepositDetailsInvalid
      || (ex.errorCode === ErrorCodeType.ModelValidation && (ex.errorKey === 'BankAccountNumber' || ex.errorKey === 'BankAccountNumberUnitary'));
  }

  showInvalidBankAccountSheet(ex) {
    const subtitle = ex && ex.realTimeValidation && ex.errorMessage
      ? ex.errorMessage
      : this.translate.instant('page.recipientDetailsPage.invalidBankAccountMessage');
    this.routeProvider.openActionSheet(this.routeProvider.pages.ModalAlertPage, {
      title: this.translate.instant('page.recipientDetailsPage.invalidBankAccountHeader'),
      subtitle,
      primaryButtonText: this.translate.instant('global.gotItButton')
    }, null);
  }

  filterCountryWiseRecipients(recipients, country) {

    if (country) {
      recipients = recipients.filter(recipient => {
        if (recipient && recipient.country)
          return recipient.country.toLowerCase() === country.toLowerCase();
        return false;
      });
    }

    return this.sortRecipients(recipients);
  }

  sortRecipients = recipients => recipients.sort((a, b) => a.firstName.localeCompare(b.firstName));

  getRecipientDebitCard(beneficiaryId: number): Promise<any> {
    return this.httpProvider.get(`${APP_CONFIG.remittanceApiBaseUrl}recipient/debit-card/${beneficiaryId}`);
  }

  getCountriesWithNewFormat() {
    try {
      const countries = this.utilsProvider.hasFeatureFlagUnsafe(FeatureFlagTypes.RecipientDetailsNewFormat);
      const parsedCountries = typeof countries === 'string' ? JSON.parse(countries) : countries;
      if (!Array.isArray(parsedCountries)) return [];
      return parsedCountries.map(c => typeof c === 'string' ? c.toUpperCase().trim() : c);

    } catch {
      return [];
    }
  }

  getLatinTreatmentCountries() {
      const countries = UtilsProvider.getFeatureFlagDataExperiment(FeatureFlagTypes.RecipientFormLatin);
      const parsedCountries = countries && typeof countries === 'string' ? JSON.parse(countries) : countries;
      if (!Array.isArray(parsedCountries)) return [];
      return parsedCountries.map(c => typeof c === 'string' ? c.toUpperCase().trim() : c);
  }

  getNoMiddleNameCountries() {
    const countries = UtilsProvider.getFeatureFlagDataExperiment(FeatureFlagTypes.RecipientFormNoMiddle);
    const parsedCountries = countries && typeof countries === 'string' ? JSON.parse(countries) : countries;
    if (!Array.isArray(parsedCountries)) return [];
    return parsedCountries.map(c => typeof c === 'string' ? c.toUpperCase().trim() : c);
  }
  


  // Returns true if VoP took over the flow (generic error / prompt) so the caller stops; false to proceed.
  async handleVopResult(recipient: any, opts: {
    amount?: any,
    currency?: any,
    onProceed: () => void,
    onRecipientUpdated: (updated: any) => void,
    onConsent: (consent: VopConsent) => void,
    onBeforePrompt?: () => void,
    onChangeDeliveryMethod?: () => void,
    analytics?: any,
    onEdit?: () => void,
  }): Promise<boolean> {
    const vop = recipient && recipient.vop ? recipient.vop : null;
    const outcome = vop ? getVopOutcome(vop) : null;
    if (vop && vop.promptRequired !== false && !outcome) {
      if (opts.onBeforePrompt) opts.onBeforePrompt();
      this.showVopGenericError();
      return true;
    }
    if (!outcome || outcome === VopOutcome.Match || vop.promptRequired === false) {
      if (outcome === VopOutcome.Match)
        this.trackVopResolved(vop, outcome, AccountValidationDecision.AutoContinue, false, false, false, this.withVopRevalidation(recipient, opts.analytics));
      return false;
    }
    if (opts.onBeforePrompt) opts.onBeforePrompt();
    const analytics = this.withVopRevalidation(recipient, opts.analytics);
    await this.showVopPrompt({
      vop,
      recipient,
      onContinue: opts.onProceed,
      onEdit: () => {
        this.trackVopResolved(vop, outcome, AccountValidationDecision.EditName, true, false, false, analytics);
        if (opts.onEdit) {
          if (recipient && recipient.id != null)
            this.vopEditedRecipients.add(recipient.id);
          opts.onEdit();
        } else {
          this.openVopNameEdit(recipient, opts.amount, opts.currency, opts.onRecipientUpdated);
        }
      },
      onChangeDeliveryMethod: () => {
        this.trackVopResolved(vop, outcome, AccountValidationDecision.ChangeDeliveryMethod, true, false, false, analytics);
        (opts.onChangeDeliveryMethod || this.popBackToCalculator)();
      },
      onConsent: (consent) => {
        this.trackVopConsent(vop, outcome, consent, analytics);
        opts.onConsent(consent);
      },
    });
    return true;
  }

  private vopEditedRecipients = new Set<any>();
  private withVopRevalidation(recipient: any, analytics: any) {
    const id = recipient && recipient.id;
    const isRevalidation = id != null && this.vopEditedRecipients.has(id);
    if (id != null)
      this.vopEditedRecipients.delete(id);
    return { ...(analytics || {}), isRevalidation };
  }

  trackVopConsent(vop: IVop, outcome: string, consent: VopConsent, analytics: any) {
    switch (consent) {
      case VopConsent.CloseMatchNameUpdated:
        return this.trackVopResolved(vop, outcome, AccountValidationDecision.UseRegisteredName, true, true, false, analytics);
      case VopConsent.CloseMatchProceededWithoutUpdate:
        return this.trackVopResolved(vop, outcome, AccountValidationDecision.KeepEnteredName, true, false, true, analytics);
      case VopConsent.NoMatchProceededWithoutUpdate:
        return this.trackVopResolved(vop, outcome, AccountValidationDecision.ContinueWithoutMatch, true, false, true, analytics);
      case VopConsent.NotAvailableProceeded:
        return this.trackVopResolved(vop, outcome, AccountValidationDecision.ContinueAnyway, true, false, true, analytics);
    }
  }

  trackVopResolved(vop: IVop, outcome: string, userDecision: string, promptShown: boolean, nameChanged: boolean, consentToProceedUnverified: boolean, analytics: any = {}) {
    this.analytics.trackEvent({
      eventName: AnalyticsEventType.AccountValidationResolved,
      analyticsData: {
        validationType: AccountValidationType.SepaVop,
        outcome,
        outcomeCode: vop ? vop.statusCode : null,
        userDecision,
        promptShown,
        nameChanged,
        consentToProceedUnverified,
        isRevalidation: !!analytics.isRevalidation,
        location: analytics.location,
        paymentMethod: analytics.paymentMethod,
        countryFrom: analytics.countryFrom,
        destinationCountry: analytics.destinationCountry,
        payoutMethod: analytics.payoutMethod,
      },
    });
  }

  private popBackToCalculator = () => {
    this.routeProvider.popTo(this.routeProvider.pages.CheckPricesPage);
    this.routeProvider.pop();
  }

  private openVopNameEdit(recipient: any, amount: any, currency: any, onUpdated: (updated: any) => void) {
    this.routeProvider.openModal(this.routeProvider.pages.RecipientDetailsPage, {
      recipient,
      canEditRecipient: true,
      enableEdit: true,
      amount,
      currency,
      isFromSelfServiceFlow: true,
      onRecipientSave: async (updated: any, viewCtrl: any) => {
        if (recipient && recipient.id != null)
          this.vopEditedRecipients.add(recipient.id);
        onUpdated(updated);
        if (updated && updated.id) {
          try {
            await this.updateRecipient(this.stripClientOnlyFields(updated));
          } catch (ex) {
            LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to persist recipient name after VoP edit');
          }
        }
        viewCtrl.dismiss();
      },
    });
  }

  // Keeps client-only fields (vop, bankAdded) out of what we persist to recipient/{id}.
  stripClientOnlyFields(recipient: any) {
    if (!recipient)
      return recipient;
    const { vop, bankAdded, ...clean } = recipient;
    return clean;
  }

  showVopPrompt(params: {
    vop: IVop,
    recipient: any,
    onContinue: () => void,
    onEdit: () => void,
    onChangeDeliveryMethod: () => void,
    onConsent?: (consent: VopConsent) => void,
  }) {
    const { vop, recipient, onContinue, onEdit, onChangeDeliveryMethod, onConsent } = params;
    const consent = (c: VopConsent) => { if (onConsent) onConsent(c); };
    switch (getVopOutcome(vop)) {
      case VopOutcome.CloseMatch:
        return this.showVopCloseMatch(recipient, vop.registeredName || {}, onContinue, consent);
      case VopOutcome.NoMatch:
        return this.showVopNoMatch(recipient, onContinue, onEdit, consent);
      case VopOutcome.NotAvailable:
        return this.showVopNotAvailable(recipient, onContinue, onChangeDeliveryMethod, consent);
      default:
        onContinue();
        return Promise.resolve();
    }
  }

  encodeForHtml(text: string): string {
    const div = document.createElement('div');
    div.appendChild(document.createTextNode(text || ''));
    return div.innerHTML;
  }

  async adoptRegisteredName(recipient: any, registeredName: IRegisteredName) {
    // Never blank the name: skip if BE sent no usable name parts.
    if (!registeredName || (!registeredName.firstName && !registeredName.lastName && !registeredName.fullName))
      return;
    recipient.firstName = registeredName.firstName || '';
    recipient.lastName1 = registeredName.lastName || '';
    recipient.lastName2 = '';
    recipient.fullName = registeredName.fullName || [registeredName.firstName, registeredName.lastName].filter(Boolean).join(' ');
    if (recipient.id) {
      try {
        await this.updateRecipient(this.stripClientOnlyFields(recipient));
      } catch (ex) {
        LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to persist recipient name after VoP close match');
      }
    }
    RmtSnackbarComponent.present(this.translate.instant('page.sendFinishPage.nameMismatchNameUpdated'));
  }

  showVopCloseMatch(recipient: any, registeredName: IRegisteredName, onContinue: () => void, consent: (c: VopConsent) => void) {
    const enteredName = [recipient.firstName, recipient.lastName1].filter(Boolean).join(' ');
    const registeredFullName = (registeredName && registeredName.fullName)
      ? registeredName.fullName
      : [registeredName && registeredName.firstName, registeredName && registeredName.lastName].filter(Boolean).join(' ');
    const bodyHtml = `<p>${this.translate.instant('page.sendFinishPage.nameMismatchDescription')}</p>`
      + `<div class="vop-name-box">`
      + `<p>${this.translate.instant('page.sendFinishPage.nameMismatchEnteredSEPA')} <b>${this.encodeForHtml(enteredName)}</b></p>`
      + `<p>${this.translate.instant('page.sendFinishPage.nameMismatchRegisteredSEPA')} <b>${this.encodeForHtml(registeredFullName)}</b></p>`
      + `</div>`;

    return new Promise<void>((resolve) => {
      this.routeProvider.openActionSheetNew(this.routeProvider.pages.ModalAlertPage, {
        img: 'assets/icon/warning-backgroud-icon.svg',
        title: this.translate.instant('page.sendFinishPage.nameCloseMatchModalTitleSEPA'),
        subtitleInnerHtml: bodyHtml,
        primaryButtonText: this.translate.instant('page.sendFinishPage.nameMismatchUseRegisteredSEPA'),
        primaryAction: async () => {
          await this.adoptRegisteredName(recipient, registeredName);
          consent(VopConsent.CloseMatchNameUpdated);
          onContinue();
          resolve();
        },
        showSecondaryButton: true,
        secondaryButtonText: this.translate.instant('page.sendFinishPage.nameMismatchKeepSEPA'),
        secondaryAction: () => {
          consent(VopConsent.CloseMatchProceededWithoutUpdate);
          onContinue();
          resolve();
        },
        dismissFromPrimary: true,
        dismissFromSecondary: true,
      });
    });
  }

  withRecipientName(key: string, recipient: any): string {
    const enteredName = [recipient.firstName, recipient.lastName1].filter(Boolean).join(' ');
    return this.translate.instant(key).replace('{{name}}', `<b>${this.encodeForHtml(enteredName)}</b>`);
  }

  showVopNoMatch(recipient: any, onContinue: () => void, onEdit: () => void, consent: (c: VopConsent) => void) {
    return new Promise<void>((resolve) => {
      this.routeProvider.openActionSheetNew(this.routeProvider.pages.ModalAlertPage, {
        img: 'assets/icon/warning-backgroud-icon.svg',
        title: this.translate.instant('page.sendFinishPage.nameMismatchModalTitleSEPA'),
        subtitleInnerHtml: `<p>${this.withRecipientName('page.sendFinishPage.nameMismatchDescriptionEnforcedSEPA', recipient)}</p>`,
        primaryButtonText: this.translate.instant('page.sendFinishPage.nameMismatchEditName'),
        primaryAction: () => {
          onEdit();
          resolve();
        },
        showSecondaryButton: true,
        secondaryButtonText: this.translate.instant('page.sendFinishPage.nameMismatchKeepSEPA'),
        secondaryAction: () => {
          consent(VopConsent.NoMatchProceededWithoutUpdate);
          onContinue();
          resolve();
        },
        dismissFromPrimary: true,
        dismissFromSecondary: true,
      });
    });
  }

  showVopNotAvailable(recipient: any, onContinue: () => void, onChangeDeliveryMethod: () => void, consent: (c: VopConsent) => void) {
    return new Promise<void>((resolve) => {
      this.routeProvider.openActionSheetNew(this.routeProvider.pages.ModalAlertPage, {
        img: 'assets/icon/warning-backgroud-icon.svg',
        title: this.translate.instant('page.sendFinishPage.nameMismatchValidationTitleSEPA'),
        subtitleInnerHtml: `<p>${this.withRecipientName('page.sendFinishPage.nameMismatchValidationBodySEPA', recipient)}</p>`,
        primaryButtonText: this.translate.instant('page.sendFinishPage.nameMismatchValidationConfirm'),
        primaryAction: () => {
          consent(VopConsent.NotAvailableProceeded);
          onContinue();
          resolve();
        },
        showSecondaryButton: true,
        secondaryButtonText: this.translate.instant('page.sendFinishPage.nameMismatchChangeDeliveryMethod'),
        secondaryAction: () => {
          onChangeDeliveryMethod();
          resolve();
        },
        dismissFromPrimary: true,
        dismissFromSecondary: true,
      });
    });
  }

  showVopGenericError() {
    this.routeProvider.push(this.routeProvider.pages.ModalAlertPage, {
      showFullPage: true,
      showCircle: false,
      newDesign: true,
      newFooterStyle: true,
      showRightClose: false,
      showBack: true,
      showHelp: true,
      img: 'assets/imgs/error-generic.svg',
      title: this.translate.instant('ComponentBankDeposit.RtvErrorTitle'),
      msg: this.translate.instant('global.tryLaterButton'),
      primaryButtonText: this.translate.instant('page.hubPage.activityButton'),
      primaryAction: () => this.utilsProvider.navigateToHomeOrFallback(this.routeProvider.pages.TabsHomePage),
    });
  }
}



// WEBPACK FOOTER //
// ./src/providers/recipient/recipient.ts